import { Injectable } from '@angular/core';
import { Action, Store } from '@ngrx/store';
import { SignalrEvent } from '@wdx/shared/utils';
import { filter, from, mergeMap, Observable } from 'rxjs';
import * as systemEventsSelectors from './system-events.selectors';
import { SystemEvent, SystemSignalrEvent } from './system-events.reducer';

@Injectable({ providedIn: 'root' })
export class SystemEventsFacade {
    constructor(private store$: Store) {}

    public getNewEvents$(timeStamp: number = Date.now()) {
        return this.store$
            .select(systemEventsSelectors.getLatestEvent)
            .pipe(
                filter(
                    (event) => event?.timeStamp && event.timeStamp > timeStamp,
                ),
            );
    }

    public getNewSystemrEvents$(timeStamp: number = Date.now()) {
        return this.store$
            .select(systemEventsSelectors.getLatestSignalrEvent)
            .pipe(
                filter(
                    (event) => event?.timeStamp && event.timeStamp > timeStamp,
                ),
            );
    }

    public getEventsOfSourceActionTypes$<T>(
        actionTypes: string[],
    ): Observable<SystemEvent<T>> {
        return this.getNewEvents$().pipe(
            filter((event) =>
                event?.sourceAction?.type
                    ? actionTypes.includes(event.sourceAction.type)
                    : undefined,
            ),
        );
    }

    public getEventsOfSignalrEvents$(SignalrEvents: SignalrEvent[]) {
        let latestEventId = 0;
        let hasSeenInitialEvents = false;
        const subscribedAt = Date.now();

        return this.store$.select(systemEventsSelectors.getSignalrEvents).pipe(
            mergeMap((events) => {
                if (!hasSeenInitialEvents) {
                    latestEventId = getLatestSignalrEventId(
                        latestEventId,
                        events,
                    );
                    hasSeenInitialEvents = true;
                }

                const newEvents = events.filter(
                    (event) =>
                        isNewSignalrEvent(event, latestEventId, subscribedAt) &&
                        SignalrEvents.includes(event.signalrEvent),
                );

                latestEventId = getLatestSignalrEventId(latestEventId, events);

                return from(newEvents);
            }),
        );
    }
}

function isNewSignalrEvent(
    event: SystemSignalrEvent,
    latestEventId: number,
    subscribedAt: number,
): boolean {
    return typeof event.eventId === 'number'
        ? event.eventId > latestEventId
        : event.timeStamp > subscribedAt;
}

function getLatestSignalrEventId(
    latestEventId: number,
    events: SystemSignalrEvent[],
): number {
    return events.reduce(
        (maxEventId, event) =>
            typeof event.eventId === 'number'
                ? Math.max(maxEventId, event.eventId)
                : maxEventId,
        latestEventId,
    );
}
